import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import InputField from "../../../../components/InputField";
import SelectField from "../../../../components/SelectField";
import Button from "../../../../components/Button";
import BackButton from "../../../../components/BackButton";
import PersonIcon from "@mui/icons-material/Person";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import "../../Common/form.css";
import "../../Admin/MasterData/vendorform.css";

const ClientOnboardingForm = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [documentErrors, setDocumentErrors] = useState({});

  // =================================================
  // PDF UPLOAD SUCCESS MESSAGE
  // =================================================

  const [documentUploadSuccess, setDocumentUploadSuccess] =
    useState({});

  const documentList = [
    {
      name: "cin_certificate",
      label: "CIN Certificate",
      description: "Company incorporation certificate",
    },
    {
      name: "gstn",
      label: "GSTN",
      description: "GST registration certificate",
    },
    {
      name: "pan",
      label: "PAN",
      description: "Company PAN document",
    },
    {
      name: "cancelled_cheque",
      label: "Cancelled Cheque",
      description: "Company bank account cheque",
    },
    {
      name: "iec",
      label: "IEC Certificate",
      description: "IEC Certificate",
    },
  ];

  const organisationTypeOptions = [
    {
      value: "Vendor",
      label: "Vendor",
    },
    {
      value: "Service Provider",
      label: "Service Provider",
    },
    {
      value: "Vendor & Service Provider",
      label: "Vendor & Service Provider",
    },
  ];

  const formik = useFormik({
    enableReinitialize: true,

    initialValues: {
      // =================================================
      // COMPANY / ORGANISATION DETAILS
      // =================================================

      organisation_name: "",
      organisation_type: "",
      gst_tan_no: "",
      pan_no: "",
      company_cin_no: "",
      iec_no: "",
      name_of_business: "",

      // =================================================
      // ADDRESS
      // =================================================

      flat_house_building: "",
      area_street_sector: "",
      country: "",
      state: "",
      pincode: "",

      // =================================================
      // POC 1
      // =================================================

      poc1_full_name: "",
      poc1_designation: "",
      poc1_email: "",
      poc1_mobile: "",
      poc1_telephone: "",
      poc1_extension: "",

      // =================================================
      // POC 2
      // =================================================

      poc2_full_name: "",
      poc2_designation: "",
      poc2_email: "",
      poc2_mobile: "",
      poc2_telephone: "",
      poc2_extension: "",

      // =================================================
      // DOCUMENTS
      // =================================================

      cin_certificate: null,
      gstn: null,
      pan: null,
      cancelled_cheque: null,
      iec: null,
    },

    validationSchema: Yup.object({
      // =================================================
      // COMPANY / ORGANISATION DETAILS
      // =================================================

      organisation_name: Yup.string()
        .trim()
        .required("Organisation Name is required"),

      organisation_type: Yup.string().required(
        "Organisation Type is required",
      ),

      gst_tan_no: Yup.string()
        .trim()
        .required("GST/TAN No. is required"),

      pan_no: Yup.string()
        .trim()
        .required("PAN No. is required"),

      company_cin_no: Yup.string()
        .trim()
        .required("CIN number is required"),

      iec_no: Yup.string()
        .trim()
        .required("IEC No. is required"),

      name_of_business: Yup.string()
        .trim()
        .required("Name of Business is required"),

      // =================================================
      // ADDRESS
      // =================================================

      flat_house_building: Yup.string()
        .trim()
        .required(
          "Flat/House No./Building/Company/Apartment is required",
        ),

      area_street_sector: Yup.string()
        .trim()
        .required(
          "Area/Street/Sector/Town/Village is required",
        ),

      country: Yup.string()
        .trim()
        .required("Country is required"),

      state: Yup.string()
        .trim()
        .required("State is required"),

      pincode: Yup.string()
        .required("Pincode is required")
        .matches(
          /^[0-9]{6}$/,
          "Pincode must contain exactly 6 digits",
        ),

      // =================================================
      // POC 1
      // =================================================

      poc1_full_name: Yup.string()
        .trim()
        .required("POC-1 Full Name is required"),

      poc1_designation: Yup.string().required(
        "Designation is Required",
      ),

      poc1_email: Yup.string()
        .email("Please enter a valid POC-1 email address")
        .required("Please enter a valid POC-1 email address"),

      poc1_mobile: Yup.string().required(
        "Mobile Number is Required",
      ),

      poc1_telephone: Yup.string().required(
        "Telephone Number is Required",
      ),

      poc1_extension: Yup.string().required(
        "Extension Number is Required",
      ),

      // =================================================
      // POC 2
      // =================================================

      poc2_full_name: Yup.string().required(
        "Full Name is Required",
      ),

      poc2_designation: Yup.string().required(
        "Designation is Required",
      ),

      poc2_email: Yup.string()
        .email("Please enter a valid POC-2 email address")
        .required("Please enter a valid POC-2 email address"),

      poc2_mobile: Yup.string().required(
        "Mobile Number is Required",
      ),

      poc2_telephone: Yup.string().required(
        "Telephone Number is Required",
      ),

      poc2_extension: Yup.string().required(
        "Extension Number is Required",
      ),
    }),

    onSubmit: async (values) => {
      try {
        setLoading(true);
        setSubmitError("");

        console.log("========== VENDOR FORM DATA ==========");

        // =================================================
        // COMPANY / ORGANISATION DETAILS
        // =================================================

        console.log("Organisation Details:", {
          organisation_name: values.organisation_name,
          organisation_type: values.organisation_type,
          gst_tan_no: values.gst_tan_no,
          pan_no: values.pan_no,
          company_cin_no: values.company_cin_no,
          iec_no: values.iec_no,
          name_of_business: values.name_of_business,

          flat_house_building: values.flat_house_building,
          area_street_sector: values.area_street_sector,
          country: values.country,
          state: values.state,
          pincode: values.pincode,
        });

        // =================================================
        // POC 1
        // =================================================

        console.log("POC 1:", {
          full_name: values.poc1_full_name,
          designation: values.poc1_designation,
          email: values.poc1_email,
          mobile: values.poc1_mobile,
          telephone: values.poc1_telephone,
          extension: values.poc1_extension,
        });

        // =================================================
        // POC 2
        // =================================================

        console.log("POC 2:", {
          full_name: values.poc2_full_name,
          designation: values.poc2_designation,
          email: values.poc2_email,
          mobile: values.poc2_mobile,
          telephone: values.poc2_telephone,
          extension: values.poc2_extension,
        });

        // =================================================
        // DOCUMENTS
        // =================================================

        console.log("========== DOCUMENTS ==========");

        documentList.forEach((doc) => {
          const file = values[doc.name];

          if (file instanceof File) {
            console.log(doc.label, {
              name: file.name,
              type: file.type,
              size: file.size,
            });
          } else {
            console.log(doc.label, "Not uploaded");
          }
        });

        await new Promise((resolve) =>
          setTimeout(resolve, 500),
        );

        alert("Vendor details saved successfully.");

        navigate("/vendor-master");
      } catch (err) {
        console.error("Error submitting vendor:", err);

        setSubmitError(
          "Something went wrong while saving vendor.",
        );
      } finally {
        setLoading(false);
      }
    },
  });

  // =================================================
  // DOCUMENT UPLOAD
  // =================================================

  const handleDocumentUpload = (event, documentName) => {
    const file = event.currentTarget.files?.[0];

    if (!file) {
      return;
    }

    // Clear previous error
    setDocumentErrors((prev) => ({
      ...prev,
      [documentName]: "",
    }));

    // =================================================
    // PDF VALIDATION
    // =================================================

    const isPdf =
      file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf");

    if (!isPdf) {
      setDocumentErrors((prev) => ({
        ...prev,
        [documentName]:
          "Only PDF files are supported.",
      }));

      // Hide success message if any
      setDocumentUploadSuccess((prev) => ({
        ...prev,
        [documentName]: false,
      }));

      formik.setFieldValue(documentName, null);

      event.currentTarget.value = "";

      return;
    }

    // =================================================
    // FILE SIZE VALIDATION
    // =================================================

    const maxSize = 5 * 1024 * 1024;

    if (file.size > maxSize) {
      setDocumentErrors((prev) => ({
        ...prev,
        [documentName]:
          "PDF file size should not exceed 5 MB.",
      }));

      // Hide success message if any
      setDocumentUploadSuccess((prev) => ({
        ...prev,
        [documentName]: false,
      }));

      formik.setFieldValue(documentName, null);

      event.currentTarget.value = "";

      return;
    }

    // =================================================
    // SUCCESSFUL UPLOAD
    // =================================================

    formik.setFieldValue(documentName, file);

    setDocumentErrors((prev) => ({
      ...prev,
      [documentName]: "",
    }));

    // Show success message
    setDocumentUploadSuccess((prev) => ({
      ...prev,
      [documentName]: true,
    }));

    // Hide success message after 1 second
    setTimeout(() => {
      setDocumentUploadSuccess((prev) => ({
        ...prev,
        [documentName]: false,
      }));
    }, 1000);

    console.log("PDF uploaded successfully:", {
      documentName,
      name: file.name,
      type: file.type,
      size: file.size,
    });

    // Reset input so same file can be selected again
    event.currentTarget.value = "";
  };

  // =================================================
  // VIEW DOCUMENT
  // =================================================

  const handleViewDocument = (file) => {
    if (!file) {
      return;
    }

    try {
      const fileUrl = URL.createObjectURL(file);

      const link = document.createElement("a");

      link.href = fileUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      setTimeout(() => {
        URL.revokeObjectURL(fileUrl);
      }, 60000);
    } catch (err) {
      console.error("Unable to preview PDF:", err);

      setSubmitError("Unable to preview this PDF.");
    }
  };

  // =================================================
  // REMOVE DOCUMENT
  // =================================================

  const handleRemoveDocument = (documentName) => {
    formik.setFieldValue(documentName, null);

    setDocumentErrors((prev) => ({
      ...prev,
      [documentName]: "",
    }));

    // Hide success message
    setDocumentUploadSuccess((prev) => ({
      ...prev,
      [documentName]: false,
    }));

    setSubmitError("");
  };

  return (
    <>
      <BackButton />

      <div className="vendor-master-page">
        <form
          onSubmit={formik.handleSubmit}
          encType="multipart/form-data"
          noValidate
        >
          {/* =================================================
              SUBMIT ERROR
          ================================================== */}

          {submitError && (
            <div
              className="alert alert-danger vendor-error"
              role="alert"
            >
              {submitError}
            </div>
          )}

          {/* =================================================
              01 ORGANISATION DETAILS
          ================================================== */}

          <fieldset className="vendor-section mt-4">
            <legend className="vendor-section-legend">
              <span className="vendor-section-icon">
                <PersonIcon fontSize="small" />
              </span>

              <span className="vendor-section-title">
                Company Details
              </span>
            </legend>

            <p className="vendor-section-description">
              Enter company registration and address
              information.
            </p>

            <div className="vendor-section-body">
              {/* =================================================
                  ORGANISATION INFORMATION
              ================================================== */}

              <div className="row g-3 mb-3">
                {/* ORGANISATION NAME */}

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Organisation Name"
                    name="organisation_name"
                    value={formik.values.organisation_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter organisation name"
                    required
                    error={
                      formik.touched.organisation_name &&
                      formik.errors.organisation_name
                    }
                  />
                </div>

                {/* ORGANISATION TYPE */}

                <div className="col-12 col-md-6 col-lg-4">
                  <SelectField
                    label="Organisation Type"
                    name="organisation_type"
                    value={formik.values.organisation_type}
                    options={organisationTypeOptions}
                    onChange={(value) =>
                      formik.setFieldValue(
                        "organisation_type",
                        value,
                      )
                    }
                    placeholder="Select organisation type"
                    required
                    error={
                      formik.touched.organisation_type &&
                      formik.errors.organisation_type
                    }
                  />
                </div>

                {/* GST/TAN */}

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="GST/TAN No."
                    name="gst_tan_no"
                    value={formik.values.gst_tan_no}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter GST/TAN number"
                    required
                    error={
                      formik.touched.gst_tan_no &&
                      formik.errors.gst_tan_no
                    }
                  />
                </div>

                {/* PAN */}

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="PAN No."
                    name="pan_no"
                    value={formik.values.pan_no}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter PAN number"
                    required
                    error={
                      formik.touched.pan_no &&
                      formik.errors.pan_no
                    }
                  />
                </div>

                {/* COMPANY CIN */}

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Company CIN No."
                    name="company_cin_no"
                    value={formik.values.company_cin_no}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter CIN number"
                    required
                    error={
                      formik.touched.company_cin_no &&
                      formik.errors.company_cin_no
                    }
                  />
                </div>

                {/* IEC NO */}

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="IEC No."
                    name="iec_no"
                    value={formik.values.iec_no}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter IEC number"
                    required
                    error={
                      formik.touched.iec_no &&
                      formik.errors.iec_no
                    }
                  />
                </div>

                {/* NAME OF BUSINESS */}

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Name of Business"
                    name="name_of_business"
                    value={formik.values.name_of_business}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter name of business"
                    required
                    error={
                      formik.touched.name_of_business &&
                      formik.errors.name_of_business
                    }
                  />
                </div>
              </div>

              {/* =================================================
                  REGISTERED ADDRESS
              ================================================== */}

              <div className="vendor-subsection">
                <div className="vendor-subsection-title">
                  Registered Address
                </div>

                <div className="row g-3">
                  {/* FLAT / HOUSE */}

                  <div className="col-12 col-lg-6">
                    <InputField
                      label="Flat/House No./Building/Company/Apartment"
                      name="flat_house_building"
                      value={
                        formik.values.flat_house_building
                      }
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="Enter address"
                      required
                      error={
                        formik.touched.flat_house_building &&
                        formik.errors.flat_house_building
                      }
                    />
                  </div>

                  {/* AREA / STREET */}

                  <div className="col-12 col-lg-6">
                    <InputField
                      label="Area/Street/Sector/Town/Village"
                      name="area_street_sector"
                      value={
                        formik.values.area_street_sector
                      }
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="Enter area/street/sector"
                      required
                      error={
                        formik.touched.area_street_sector &&
                        formik.errors.area_street_sector
                      }
                    />
                  </div>

                  {/* COUNTRY */}

                  <div className="col-12 col-md-4">
                    <InputField
                      label="Country"
                      name="country"
                      value={formik.values.country}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="Enter country"
                      required
                      error={
                        formik.touched.country &&
                        formik.errors.country
                      }
                    />
                  </div>

                  {/* STATE */}

                  <div className="col-12 col-md-4">
                    <InputField
                      label="State"
                      name="state"
                      value={formik.values.state}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="Enter state"
                      required
                      error={
                        formik.touched.state &&
                        formik.errors.state
                      }
                    />
                  </div>

                  {/* PINCODE */}

                  <div className="col-12 col-md-4">
                    <InputField
                      label="Pincode"
                      name="pincode"
                      value={formik.values.pincode}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="Enter pincode"
                      error={
                        formik.touched.pincode &&
                        formik.errors.pincode
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </fieldset>

          {/* =================================================
              02 CONTACT DETAILS
          ================================================== */}

          <fieldset className="vendor-section mt-4">
            <legend className="vendor-section-legend">
              <span className="vendor-section-icon">
                <ContactMailIcon fontSize="small" />
              </span>

              <span className="vendor-section-title">
                Contact Details
              </span>
            </legend>

            <p className="vendor-section-description">
              Add authorised and assigned points of contact.
            </p>

            <div className="vendor-section-body">
              {/* =================================================
                  POC 1
              ================================================== */}

              <div className="vendor-poc-header">
                <div className="vendor-poc-heading">
                  <span className="vendor-poc-label">
                    POC - 1
                  </span>

                  <span className="vendor-poc-badge authorised">
                    Authorised
                  </span>
                </div>
              </div>

              <div className="row g-3">
                {/* FULL NAME */}

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Full Name"
                    name="poc1_full_name"
                    value={formik.values.poc1_full_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter full name"
                    required
                    error={
                      formik.touched.poc1_full_name &&
                      formik.errors.poc1_full_name
                    }
                  />
                </div>

                {/* DESIGNATION */}

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Designation"
                    name="poc1_designation"
                    value={formik.values.poc1_designation}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter designation"
                    error={
                      formik.touched.poc1_designation &&
                      formik.errors.poc1_designation
                    }
                  />
                </div>

                {/* EMAIL */}

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Mail ID"
                    name="poc1_email"
                    type="email"
                    value={formik.values.poc1_email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter email address"
                    error={
                      formik.touched.poc1_email &&
                      formik.errors.poc1_email
                    }
                  />
                </div>

                {/* MOBILE */}

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Mobile No."
                    name="poc1_mobile"
                    type="tel"
                    value={formik.values.poc1_mobile}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter mobile number"
                    error={
                      formik.touched.poc1_mobile &&
                      formik.errors.poc1_mobile
                    }
                  />
                </div>

                {/* TELEPHONE */}

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Telephone No."
                    name="poc1_telephone"
                    type="tel"
                    value={formik.values.poc1_telephone}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter telephone number"
                    error={
                      formik.touched.poc1_telephone &&
                      formik.errors.poc1_telephone
                    }
                  />
                </div>

                {/* EXTENSION */}

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Extension No."
                    name="poc1_extension"
                    value={formik.values.poc1_extension}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter extension number"
                    error={
                      formik.touched.poc1_extension &&
                      formik.errors.poc1_extension
                    }
                  />
                </div>
              </div>

              {/* =================================================
                  POC 2
              ================================================== */}

              <div className="vendor-poc-header">
                <div className="vendor-poc-heading">
                  <span className="vendor-poc-label">
                    POC - 2
                  </span>

                  <span className="vendor-poc-badge assigned">
                    Assigned
                  </span>
                </div>
              </div>

              <div className="row g-3">
                {/* FULL NAME */}

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Full Name"
                    name="poc2_full_name"
                    value={formik.values.poc2_full_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter full name"
                    error={
                      formik.touched.poc2_full_name &&
                      formik.errors.poc2_full_name
                    }
                  />
                </div>

                {/* DESIGNATION */}

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Designation"
                    name="poc2_designation"
                    value={formik.values.poc2_designation}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter designation"
                    error={
                      formik.touched.poc2_designation &&
                      formik.errors.poc2_designation
                    }
                  />
                </div>

                {/* EMAIL */}

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Mail ID"
                    name="poc2_email"
                    type="email"
                    value={formik.values.poc2_email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter email address"
                    error={
                      formik.touched.poc2_email &&
                      formik.errors.poc2_email
                    }
                  />
                </div>

                {/* MOBILE */}

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Mobile No."
                    name="poc2_mobile"
                    type="tel"
                    value={formik.values.poc2_mobile}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter mobile number"
                    error={
                      formik.touched.poc2_mobile &&
                      formik.errors.poc2_mobile
                    }
                  />
                </div>

                {/* TELEPHONE */}

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Telephone No."
                    name="poc2_telephone"
                    type="tel"
                    value={formik.values.poc2_telephone}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter telephone number"
                    error={
                      formik.touched.poc2_telephone &&
                      formik.errors.poc2_telephone
                    }
                  />
                </div>

                {/* EXTENSION */}

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Extension No."
                    name="poc2_extension"
                    value={formik.values.poc2_extension}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter extension number"
                    error={
                      formik.touched.poc2_extension &&
                      formik.errors.poc2_extension
                    }
                  />
                </div>
              </div>
            </div>
          </fieldset>

          {/* =================================================
              03 SUPPORTING DOCUMENTS
          ================================================== */}

          <fieldset className="vendor-section mt-4">
            <legend className="vendor-section-legend">
              <span className="vendor-section-icon">
                <ContactSupportIcon fontSize="small" />
              </span>

              <span className="vendor-section-title">
                Supporting Docs Checklist
              </span>
            </legend>

            <p className="vendor-section-description">
              Upload the required documents in PDF format.
            </p>

            <div className="vendor-section-body">
              <div className="row g-3 mb-3">
                {documentList.map((doc) => {
                  const uploadedFile =
                    formik.values[doc.name];

                  const documentError =
                    documentErrors[doc.name];

                  const uploadSuccess =
                    documentUploadSuccess[doc.name];

                  return (
                    <div
                      className="col-12 col-md-6 col-lg-4"
                      key={doc.name}
                    >
                      <div
                        className={`vendor-document-upload-card ${
                          uploadedFile ? "uploaded" : ""
                        } ${
                          documentError ? "has-error" : ""
                        }`}
                      >
                        {/* DOCUMENT INFORMATION */}

                        <div className="vendor-document-info">
                          <div
                            className={`vendor-document-icon ${
                              uploadedFile
                                ? "success"
                                : documentError
                                  ? "error"
                                  : ""
                            }`}
                          >
                            {uploadedFile ? "✓" : "PDF"}
                          </div>

                          <div className="vendor-document-title">
                            <span>{doc.label}</span>

                            <small>
                              {doc.description}
                            </small>
                          </div>
                        </div>

                        {/* FILE INPUT */}

                        <input
                          type="file"
                          id={`document-${doc.name}`}
                          accept="application/pdf,.pdf"
                          style={{
                            display: "none",
                          }}
                          onChange={(event) =>
                            handleDocumentUpload(
                              event,
                              doc.name,
                            )
                          }
                        />

                        {/* DOCUMENT ERROR */}

                        {documentError && (
                          <div
                            className="vendor-document-error"
                            role="alert"
                          >
                            {documentError}
                          </div>
                        )}

                        {/* UPLOAD SUCCESS MESSAGE */}
                        {/* Shows only for 1 second */}

                        {uploadSuccess && (
                          <div className="vendor-uploaded-status">
                            <span>✓</span>
                            PDF uploaded successfully
                          </div>
                        )}

                        {/* ACTIONS */}

                        <div className="vendor-document-actions">
                          {!uploadedFile ? (
                            <label
                              htmlFor={`document-${doc.name}`}
                              className="vendor-upload-btn"
                            >
                              Upload PDF
                            </label>
                          ) : (
                            <>
                              {/* VIEW */}

                              <button
                                type="button"
                                className="vendor-view-btn"
                                onClick={() =>
                                  handleViewDocument(
                                    uploadedFile,
                                  )
                                }
                              >
                                View
                              </button>

                              {/* REPLACE */}

                              <label
                                htmlFor={`document-${doc.name}`}
                                className="vendor-replace-btn"
                              >
                                Replace
                              </label>

                              {/* REMOVE */}

                              <button
                                type="button"
                                className="vendor-remove-btn"
                                onClick={() =>
                                  handleRemoveDocument(
                                    doc.name,
                                  )
                                }
                              >
                                Remove
                              </button>
                            </>
                          )}
                        </div>

                        {/* FILE NAME */}

                        {uploadedFile && (
                          <div
                            className="vendor-file-name"
                            title={uploadedFile.name}
                          >
                            📄 {uploadedFile.name}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* =================================================
                SUBMIT BUTTON
            ================================================== */}

            <div className="d-flex align-items-center justify-content-end py-2 me-3">
              <Button
                type="submit"
                variant="primary"
                text="Submit"
                loading={loading}
              />
            </div>
          </fieldset>
        </form>
      </div>
    </>
  );
};

export default ClientOnboardingForm;

